/**
 * Sales Order Create/Edit Form
 * Version: 1.0.0
 * Created: 2025-12-07
 *
 * Form for creating and editing sales orders with dynamic pricing
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    TextField,
    Autocomplete,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
} from '@mui/material';
import { Add, Delete, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { salesOrdersAPI } from '../../../api/salesOrders';
import { CreateSORequest, OrderSource, SalesOrderStatus } from '../../../types/SalesOrder';
import { zohoCustomerAPI } from '../../../api/zohoCustomer';
import { zohoItemAPI } from '../../../api/zohoItem';
import { inventoryAPI } from '../../../api/inventory';
import { getTodayISO } from '../../../utils/dateUtils';

interface SOItemRow {
    item_id: number | null;
    item_name: string;
    quantity: number;
    unit_price: number;
    total: number;
}

interface CustomerOption {
    id: number;
    name: string;
}

interface ItemOption {
    id: number;
    name: string;
    rate?: number;
}

const SalesOrderForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [customerId, setCustomerId] = useState<number | null>(null);
    const [orderDate, setOrderDate] = useState<Dayjs | null>(dayjs());
    const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(null);
    const [source, setSource] = useState<OrderSource>(OrderSource.MANUAL);
    const [status, setStatus] = useState<SalesOrderStatus>(SalesOrderStatus.DRAFT);
    const [items, setItems] = useState<SOItemRow[]>([]);
    const [notes, setNotes] = useState('');
    const [stockWarnings, setStockWarnings] = useState<string[]>([]);
    const [soNumber, setSoNumber] = useState<string>('');  // Editable SO number

    // Data Options
    const [customers, setCustomers] = useState<CustomerOption[]>([]);
    const [availableItems, setAvailableItems] = useState<ItemOption[]>([]);

    // Load initial data
    // Load initial data
    useEffect(() => {
        const loadCommonData = async () => {
            try {
                // Load items
                const itemsData = await zohoItemAPI.getItems();
                const itemList = itemsData.items || [];
                setAvailableItems(itemList.map((i: any) => ({ id: i.id, name: i.name, rate: i.rate })));

                // Initial Customer Load (top 50)
                const customersData = await zohoCustomerAPI.getItems({ limit: 50 });
                const customerList = customersData.customers || customersData.items || [];
                setCustomers(customerList.map((c: any) => ({ id: c.id, name: c.contact_name || c.name })));
            } catch (error: any) {
                console.error('Failed to load common data:', error);
            }
        };

        const loadOrderData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const order = await salesOrdersAPI.getOrder(id!);
                setCustomerId(order.customer_id);
                setOrderDate(dayjs(order.order_date));
                setDeliveryDate(order.delivery_date ? dayjs(order.delivery_date) : null);
                setSource(order.order_source as OrderSource);
                setStatus(order.status);
                setNotes(order.notes || '');
                setSoNumber(order.so_number || ''); // Load existing SO number

                // Pre-load the specific customer if not in list
                if (order.customer_id) {
                    try {
                        const cust = await zohoCustomerAPI.getItem(order.customer_id);
                        if (cust) {
                            const custOpt = { id: cust.id, name: cust.contact_name || cust.name };
                            setCustomers(prev => {
                                if (!prev.find(c => c.id === custOpt.id)) {
                                    return [custOpt, ...prev];
                                }
                                return prev;
                            });
                        }
                    } catch (e) {
                        console.error("Could not load order customer details", e);
                    }
                }

                const mappedItems = order.items.map(item => ({
                    item_id: item.item_id,
                    item_name: item.item_name,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    total: item.line_total
                }));
                setItems(mappedItems);

            } catch (error: any) {
                console.error('Failed to load order:', error);
                enqueueSnackbar('Failed to load order details', { variant: 'error' });
                navigate('/outward/sales-orders');
            } finally {
                setLoading(false);
            }
        };

        const loadNextSoNumber = async () => {
            if (!id) {  // Only for new orders
                try {
                    console.log('🔄 Fetching SO sequence settings...');
                    const settings = await salesOrdersAPI.getSequenceSettings();
                    console.log('✅ Sequence settings received:', settings);

                    if (settings && settings.example_number) {
                        setSoNumber(settings.example_number);
                        console.log('✅ SO Number set to:', settings.example_number);
                    } else {
                        console.warn('⚠️ No example_number in settings:', settings);
                        // Set a fallback placeholder
                        setSoNumber('SO/YYYY/00001');
                    }
                } catch (error: any) {
                    console.error('❌ Failed to load SO number:', error);
                    console.error('❌ Error details:', error.response?.data);
                    console.error('❌ Status:', error.response?.status);
                    // Set a fallback placeholder on error
                    setSoNumber('SO/YYYY/00001');
                }
            }
        };

        loadCommonData();
        loadOrderData();
        loadNextSoNumber();
    }, [id, enqueueSnackbar, navigate]);

    // specific search handler
    const handleCustomerSearch = async (_event: any, value: string) => {
        if (!value) return;
        try {
            // Simple debounce could be added here or use a library, 
            // for now relies on user typing speed / Autocomplete debounce prop if available (MUI Autocomplete doesn't have built-in debounce for onInputChange, so we might fire many requests. 
            // Implementing simple timeout debounce)

            const search = async () => {
                const data = await zohoCustomerAPI.searchCustomers(value);
                const list = data.customers || data.items || [];
                setCustomers(list.map((c: any) => ({ id: c.id, name: c.contact_name || c.name })));
            };

            // Basic debounce implementation using a ref would be better, but for simplicity/speed in this context:
            // Let's rely on the fact that this runs on every keystroke. 
            // A better production approach: use useMemo/useCallback with debounce from lodash.
            // For this fix, I'll just call it.
            search();

        } catch (error) {
            console.error("Search failed", error);
        }
    };

    // ... (rest of code) ...

    /* IN RENDER */
    /*
     <Autocomplete
        options={customers}
        getOptionLabel={(option) => option.name}
        value={customers.find((c) => c.id === customerId) || null}
        onChange={(_, value) => setCustomerId(value?.id || null)}
        onInputChange={handleCustomerSearch} // Add this
        filterOptions={(x) => x} // Disable client-side filtering
        renderInput={(params) => <TextField {...params} label="Customer *" required />}
        sx={{ flex: 1, minWidth: 250 }}
        disabled={isEdit}
    />
    */

    // Fetch price when Item + Customer selected
    const fetchItemPrice = async (itemId: number, custId: number): Promise<number | null> => {
        try {
            const result = await salesOrdersAPI.getItemPrice(custId, itemId, orderDate?.format('YYYY-MM-DD') || getTodayISO());
            return result.price;
        } catch (error) {
            console.log('No specific price found, falling back to default.');
            return null; // Fallback handled by caller
        }
    };

    // Add item row
    const addItemRow = () => {
        setItems([
            ...items,
            {
                item_id: null,
                item_name: '',
                quantity: 1,
                unit_price: 0,
                total: 0,
            },
        ]);
    };

    // Remove item row
    const removeItemRow = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    // Handle Item Selection
    const handleItemSelect = async (index: number, item: ItemOption | null) => {
        if (!item) return;

        const updatedItems = [...items];
        updatedItems[index].item_id = item.id;
        updatedItems[index].item_name = item.name;

        // Auto-fetch price
        let price = item.rate || 0; // Default
        if (customerId) {
            const customPrice = await fetchItemPrice(item.id, customerId);
            if (customPrice !== null) {
                price = customPrice;
            }
        }

        updatedItems[index].unit_price = price;
        updateItemTotal(updatedItems, index);
        setItems(updatedItems);
    };

    // Handle Quantity/Price Change
    const handleItemChange = (index: number, field: keyof SOItemRow, value: any) => {
        const updatedItems = [...items];
        (updatedItems[index] as any)[field] = value;
        updateItemTotal(updatedItems, index);
        setItems(updatedItems);
    };

    const updateItemTotal = (list: SOItemRow[], index: number) => {
        const row = list[index];
        row.total = (row.quantity || 0) * (row.unit_price || 0);
    };

    // Calculate totals
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const taxRate = 0; // Implement tax logic if needed
    const taxAmount = subtotal * taxRate;
    const grandTotal = subtotal + taxAmount;

    // Validate
    const validateForm = () => {
        if (!customerId) return 'Please select a customer';
        if (!orderDate) return 'Please select order date';
        if (items.length === 0) return 'Please add at least one item';
        for (const item of items) {
            if (!item.item_id) return 'All items must be selected';
            if (item.quantity <= 0) return 'Quantity must be positive';
        }
        return null;
    };

    // Check stock availability
    const checkStockAvailability = async () => {
        const warnings: string[] = [];
        for (const item of items) {
            if (!item.item_id) continue;
            try {
                const availability = await inventoryAPI.checkAvailability({
                    item_id: item.item_id,
                    quantity: item.quantity,
                    location: 'packed_warehouse'
                });
                if (!availability.available || availability.net_available < item.quantity) {
                    warnings.push(
                        `${item.item_name}: Only ${availability.net_available}kg available (need ${item.quantity}kg)`
                    );
                }
            } catch (e) {
                console.error('Stock check failed for item', item.item_id, e);
            }
        }
        setStockWarnings(warnings);
        return warnings;
    };

    // Submit
    const handleSubmit = async () => {
        const error = validateForm();
        if (error) {
            enqueueSnackbar(error, { variant: 'error' });
            return;
        }

        // Check stock availability before submitting
        const warnings = await checkStockAvailability();
        if (warnings.length > 0) {
            const proceed = window.confirm(
                `Stock shortfall detected:\n${warnings.join('\n')}\n\nCreate order anyway? (Stock will be reserved when available)`
            );
            if (!proceed) return;
        }

        setSubmitting(true);
        try {
            const payload: CreateSORequest = {
                customer_id: customerId!,
                order_date: orderDate?.format('YYYY-MM-DD') || getTodayISO(),
                delivery_date: deliveryDate?.format('YYYY-MM-DD') || undefined,
                order_source: source,
                so_number: soNumber || undefined, // Include SO number if provided
                items: items.map(i => ({
                    item_id: i.item_id!,
                    quantity: i.quantity,
                    unit_price: i.unit_price,
                    total_price: i.total,
                    notes: undefined
                })),
                notes: notes || undefined
            };

            if (isEdit && id) {
                // @ts-ignore
                await salesOrdersAPI.updateOrder(id, payload);
                enqueueSnackbar('Sales Order updated successfully', { variant: 'success' });
            } else {
                await salesOrdersAPI.createOrder(payload);
                enqueueSnackbar('Sales Order created successfully', { variant: 'success' });
            }
            navigate('/outward/sales-orders');

        } catch (error: any) {
            console.error('Failed to save order:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Failed to save order', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate('/outward/sales-orders')}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1">
                    {isEdit ? 'Edit Sales Order' : 'Create Sales Order'}
                </Typography>
            </Box>

            <Card sx={{ p: 3, mb: 3 }}>
                {/* Stock Warnings */}
                {stockWarnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Stock Availability Issues:</strong>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                            {stockWarnings.map((warning, idx) => (
                                <li key={idx}>{warning}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Top Section */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                    {!isEdit && (
                        <TextField
                            label="SO Number"
                            value={soNumber}
                            onChange={(e) => setSoNumber(e.target.value)}
                            placeholder="SO/YYYY/00001"
                            helperText="Auto-generated (editable)"
                            sx={{ width: 180 }}
                        />
                    )}
                    <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => option.name}
                        value={customers.find((c) => c.id === customerId) || null}
                        onChange={(_, value) => setCustomerId(value?.id || null)}
                        onInputChange={handleCustomerSearch} // Add this
                        filterOptions={(x) => x} // Disable client-side filtering
                        renderInput={(params) => <TextField {...params} label="Customer *" required />}
                        sx={{ flex: 1, minWidth: 250 }}
                        disabled={isEdit}
                    />

                    <DatePicker
                        label="Order Date *"
                        value={orderDate}
                        onChange={(newValue) => setOrderDate(newValue)}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                required: true,
                                sx: { width: 180 },
                                fullWidth: true,
                                onClick: (e) => {
                                    // Click input opens calendar
                                    const button = e.currentTarget.querySelector('button');
                                    if (button) button.click();
                                }
                            }
                        }}
                        disabled={isEdit}
                    />

                    <DatePicker
                        label="Delivery Date *"
                        value={deliveryDate}
                        onChange={(newValue) => setDeliveryDate(newValue)}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                required: true,
                                sx: { width: 180 },
                                fullWidth: true,
                                onClick: (e) => {
                                    // Click input opens calendar
                                    const button = e.currentTarget.querySelector('button');
                                    if (button) button.click();
                                }
                            }
                        }}
                    />

                    <FormControl sx={{ width: 200 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={source}
                            label="Source"
                            onChange={(e) => setSource(e.target.value as OrderSource)}
                        >
                            <MenuItem value={OrderSource.MANUAL}>Manual</MenuItem>
                            <MenuItem value={OrderSource.WHATSAPP}>WhatsApp</MenuItem>
                            <MenuItem value={OrderSource.EMAIL}>Email</MenuItem>
                            <MenuItem value={OrderSource.WEBSITE}>Website</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ width: 200 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            label="Status"
                            onChange={(e) => setStatus(e.target.value as SalesOrderStatus)}
                            disabled={!isEdit} // New orders are Draft by default usually
                        >
                            <MenuItem value={SalesOrderStatus.DRAFT}>Draft</MenuItem>
                            <MenuItem value={SalesOrderStatus.CONFIRMED}>Confirmed</MenuItem>
                            <MenuItem value={SalesOrderStatus.COMPLETED}>Completed</MenuItem>
                            <MenuItem value={SalesOrderStatus.CANCELLED}>Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Items Table */}
                <Typography variant="h6" sx={{ mb: 2 }}>Order Items</Typography>
                <Box sx={{ overflowX: 'auto', mb: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width="5%">#</TableCell>
                                <TableCell width="40%">Item</TableCell>
                                <TableCell width="15%">Quantity</TableCell>
                                <TableCell width="15%">Unit Price</TableCell>
                                <TableCell width="15%">Total</TableCell>
                                <TableCell width="10%">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            options={availableItems}
                                            getOptionLabel={(option) => option.name}
                                            value={availableItems.find(i => i.id === item.item_id) || null}
                                            onChange={(_, value) => handleItemSelect(index, value)}
                                            renderInput={(params) => <TextField {...params} size="small" placeholder="Select Item" />}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={item.quantity}
                                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={item.unit_price}
                                            onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 0 }}
                                        />
                                    </TableCell>
                                    <TableCell>₹{Number(item.total).toFixed(2)}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" color="error" onClick={() => removeItemRow(index)}>
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="text.secondary">No items added</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>

                <Button startIcon={<Add />} onClick={addItemRow} variant="outlined" sx={{ mb: 3 }}>
                    Add Item
                </Button>

                {/* Footer Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ width: '50%', minWidth: 300 }}>
                        <TextField
                            label="Notes / Internal Comments"
                            multiline
                            rows={3}
                            fullWidth
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Box>
                    <Box sx={{ minWidth: 250, textAlign: 'right' }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>Subtotal: ₹{Number(subtotal).toFixed(2)}</Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>Tax (0%): ₹{Number(taxAmount).toFixed(2)}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Grand Total: ₹{Number(grandTotal).toFixed(2)}</Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/outward/sales-orders')}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        Save Order
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};

export default SalesOrderForm;
